// Client-side mirror of the server calc engine's phase-hours math
// (server/services/estimateCalc.js) so the phase editors can show live
// total billable ST/OT hours as the user types. Keep the two in sync —
// the server remains the source of truth for money.

export const WORK_DAYS_OPTIONS = [
  { value: 'all',      label: 'All 7 days' },
  { value: 'mon_fri',  label: 'Mon–Fri' },
  { value: 'mon_thu',  label: 'Mon–Thu' },
  { value: 'mon_sat',  label: 'Mon–Sat' },
  { value: 'rot_13_1', label: '13 on / 1 off' },
  { value: 'rot_13_1_stag', label: '13 on / 1 off, staggered' },
];

// Rotation: 13 on / 1 off counted from the phase start (positional). The off
// day books no labor but the crew is still onsite — per diem covers it even
// in 'work_days' mode. Mirror of server estimateCalc.js ROTATION_13_1.
const ROTATION_13_1 = 'rot_13_1';
// Staggered 13/1 rests each head on its own day (server staggerPlan, which
// spans every line). This per-line preview books it as plain 13/1: same one
// rest day per head per 14, so totals match within a partial last cycle.
const ROTATION_13_1_STAG = 'rot_13_1_stag';

const WORK_DAY_SETS = {
  all:     new Set([0, 1, 2, 3, 4, 5, 6]),
  mon_fri: new Set([1, 2, 3, 4, 5]),
  mon_thu: new Set([1, 2, 3, 4]),
  mon_sat: new Set([1, 2, 3, 4, 5, 6]),
};

// ---- Phase / line date-range helpers (shared by the estimate + request phase
// editors and the equipment date inputs). All ISO YYYY-MM-DD string math, with
// inclusive day counts (a 1-day range counts as 1 day). ----
export const addDaysISO = (iso, days) => {
  const base = iso ? String(iso).slice(0, 10) : '';
  if (!base) return '';
  const d = new Date(base + 'T12:00:00Z');
  if (isNaN(d)) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
export const dayCountISO = (startRaw, endRaw) => {
  const s = startRaw ? String(startRaw).slice(0, 10) : '';
  const e = endRaw ? String(endRaw).slice(0, 10) : '';
  if (!s || !e) return 0;
  const ms = new Date(e + 'T12:00:00Z') - new Date(s + 'T12:00:00Z');
  return ms >= 0 ? Math.round(ms / 86400000) + 1 : 0;
};
// end = start + (days - 1), inclusive. Returns '' unless BOTH a start date AND a
// positive day count are present — so an end date never auto-populates from a
// click alone, only when a number of days is entered (George 2026-06-19).
export const endFromDays = (startRaw, days) => {
  const start = startRaw ? String(startRaw).slice(0, 10) : '';
  const num = Math.floor(Number(days));
  if (!start || !Number.isFinite(num) || num < 1) return '';
  return addDaysISO(start, num - 1);
};

const n = (v) => Number(v) || 0;

// Client mirror of estimateCalc.js peakHeadcount/positionsFor — keep in sync.
// Peak concurrent headcount on a labor line = MAX over phases of day+night.
export function peakHeadcount(phases) {
  let peak = 0;
  for (const phase of (phases || [])) {
    const hc = Math.max(0, Math.floor(n(phase.day_hc))) + Math.max(0, Math.floor(n(phase.night_hc)));
    if (hc > peak) peak = hc;
  }
  return peak;
}

// People a labor line bills per diem + mobilization for. Derived from the
// phases, with the typed qty_positions as a floor (rate-only / per-diem-only
// lines have no staffed phases and keep their typed value).
export function positionsFor(line) {
  const typed = Math.max(1, Math.floor(n(line?.qty_positions)));
  // Rate-only lines quote an unstaffed position — they book no hours, so their
  // phases never inflate positions.
  if (line?.rate_only) return typed;
  return Math.max(typed, peakHeadcount(line?.phases));
}

function isoWeekKey(d) {
  const dow = d.getUTCDay() || 7;
  const thu = new Date(d);
  thu.setUTCDate(d.getUTCDate() + 4 - dow);
  const jan4 = new Date(Date.UTC(thu.getUTCFullYear(), 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() + 1 - jan4Dow);
  const weekNo = Math.round((thu - week1Mon) / (7 * 86400000)) + 1;
  return `${thu.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function walkDays(startRaw, endRaw, workDays) {
  const days = [];
  // Dates may arrive as full ISO timestamps from the API — normalize to YYYY-MM-DD.
  const start = startRaw ? String(startRaw).slice(0, 10) : null;
  const end = endRaw ? String(endRaw).slice(0, 10) : null;
  if (!start || !end) return days;
  const s = new Date(start + 'T12:00:00Z');
  const e = new Date(end + 'T12:00:00Z');
  if (s > e || isNaN(s) || isNaN(e)) return days;
  const cur = new Date(s);
  if (workDays === ROTATION_13_1 || workDays === ROTATION_13_1_STAG) {
    let i = 0;
    while (cur <= e) {
      if (i % 14 !== 13) days.push(new Date(cur));
      i++;
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return days;
  }
  const allowed = WORK_DAY_SETS[workDays] || WORK_DAY_SETS.all;
  while (cur <= e) {
    if (allowed.has(cur.getUTCDay())) days.push(new Date(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}

// Total ST/OT hours across a line's phases (all headcount combined).
// otRule: 'weekly_over_40' | 'daily_over_8' | 'all_st'.
export function phaseHoursPreview(phases, otRule = 'weekly_over_40', priorWeekHours = 0) {
  let st = 0, ot = 0, workDays = 0;
  const weekState = new Map(); // weekKey -> Map(personKey -> hrs)
  // Hours each head already worked in the line's first week before it starts
  // (an FCO extending a crew mid-week) — mirrors server newWeekState().
  const prior = Math.max(0, n(priorWeekHours));
  let seedWeek = null;
  if (prior) {
    let first = null;
    for (const phase of (phases || [])) {
      if (n(phase.hours_per_day) === 0 || Math.floor(n(phase.day_hc)) + Math.floor(n(phase.night_hc)) <= 0) continue;
      const d = walkDays(phase.start_date, phase.end_date, phase.work_days)[0];
      if (d && (!first || d < first)) first = d;
    }
    if (first) seedWeek = isoWeekKey(first);
  }
  let minStart = null, maxEnd = null; // calendar span for 'calendar' per diem

  for (const phase of (phases || [])) {
    const hpd = n(phase.hours_per_day);
    const dayHC = Math.max(0, Math.floor(n(phase.day_hc)));
    const nightHC = Math.max(0, Math.floor(n(phase.night_hc)));
    if (hpd === 0 || dayHC + nightHC === 0) continue;

    const ps = phase.start_date ? String(phase.start_date).slice(0, 10) : null;
    const pe = phase.end_date ? String(phase.end_date).slice(0, 10) : null;
    if (ps && pe && ps <= pe) {
      if (!minStart || ps < minStart) minStart = ps;
      if (!maxEnd || pe > maxEnd) maxEnd = pe;
    }

    const days = walkDays(phase.start_date, phase.end_date, phase.work_days);
    workDays += days.length;
    for (const d of days) {
      const wk = isoWeekKey(d);
      for (const [prefix, hc] of [['day', dayHC], ['night', nightHC]]) {
        for (let p = 0; p < hc; p++) {
          if (otRule === 'all_st') { st += hpd; continue; }
          if (otRule === 'daily_over_8') {
            st += Math.min(hpd, 8); ot += Math.max(hpd - 8, 0); continue;
          }
          // weekly_over_40
          const personKey = `${prefix}-${p}`;
          if (!weekState.has(wk)) weekState.set(wk, new Map());
          const wkMap = weekState.get(wk);
          const already = wkMap.has(personKey) ? wkMap.get(personKey) : (wk === seedWeek ? prior : 0);
          const available = Math.max(40 - already, 0);
          const stPart = Math.min(hpd, available);
          st += stPart; ot += hpd - stPart;
          wkMap.set(personKey, already + hpd);
        }
      }
    }
  }
  const r2 = (v) => Math.round(v * 100) / 100;
  const calendarDays = (minStart && maxEnd)
    ? Math.round((new Date(maxEnd + 'T12:00:00Z') - new Date(minStart + 'T12:00:00Z')) / 86400000) + 1
    : 0;
  return { st: r2(st), ot: r2(ot), workDays, calendarDays };
}

// Per-craft rollup of billable man-hours and headcount, split by shift
// (Day / Night) and by phase (Pre-TA / Mechanical / Post-TA). Total man-hours
// (ST+OT) is OT-rule-independent, so this matches the server line totals from
// estimateCalc.js without needing the weekly-OT split. Lines aggregate by BASE
// craft (a trailing "(Day)/(Night)" suffix is stripped), so both shift lines of
// the same craft share one row and the Day/Night columns mean something.
// Rate-only lines are excluded (they book no hours).
const PHASE_ORDER = ['Pre-TA', 'Mechanical', 'Post-TA'];

// Strip a trailing shift token so "Rescue Tech 1 (Night)" and "(Day)" group as
// one "Rescue Tech 1" — the Day/Night columns already carry the shift split.
function baseCraftName(name) {
  return String(name || '').replace(/[\s(]*\b(?:day|night)\b\)?\s*$/i, '').trim() || '(unnamed)';
}

export function craftRollup(laborLines) {
  const crafts = new Map(); // craft -> { craft, byPhase: Map(phase -> {dayHC,nightHC,dayHrs,nightHrs}) }
  const phasesSeen = new Set();

  for (const line of (laborLines || [])) {
    if (line.rate_only) continue;
    const craft = baseCraftName(line.craft);
    if (!crafts.has(craft)) crafts.set(craft, { craft, byPhase: new Map() });
    const entry = crafts.get(craft);

    for (const phase of (line.phases || [])) {
      const hpd = n(phase.hours_per_day);
      const dayHC = Math.max(0, Math.floor(n(phase.day_hc)));
      const nightHC = Math.max(0, Math.floor(n(phase.night_hc)));
      if (hpd === 0 || dayHC + nightHC === 0) continue;
      const days = walkDays(phase.start_date, phase.end_date, phase.work_days).length;
      if (days === 0) continue;

      const phName = phase.phase || 'Other';
      phasesSeen.add(phName);
      if (!entry.byPhase.has(phName)) entry.byPhase.set(phName, { dayHC: 0, nightHC: 0, dayHrs: 0, nightHrs: 0 });
      const cell = entry.byPhase.get(phName);
      cell.dayHC += dayHC;                       // bodies of this craft on day shift this phase
      cell.nightHC += nightHC;
      cell.dayHrs += days * hpd * dayHC;         // total day man-hours this phase
      cell.nightHrs += days * hpd * nightHC;
    }
  }

  // Phases in standard order, then any non-standard ones, limited to those used.
  const phases = [
    ...PHASE_ORDER.filter(p => phasesSeen.has(p)),
    ...[...phasesSeen].filter(p => !PHASE_ORDER.includes(p)),
  ];

  const r1 = (v) => Math.round(v * 10) / 10;
  const rows = [];
  for (const { craft, byPhase } of crafts.values()) {
    const cells = {};
    let tDayHrs = 0, tNightHrs = 0, peakDayHC = 0, peakNightHC = 0;
    for (const ph of phases) {
      const c = byPhase.get(ph) || { dayHC: 0, nightHC: 0, dayHrs: 0, nightHrs: 0 };
      cells[ph] = { dayHC: c.dayHC, nightHC: c.nightHC, dayHrs: r1(c.dayHrs), nightHrs: r1(c.nightHrs) };
      tDayHrs += c.dayHrs; tNightHrs += c.nightHrs;
      peakDayHC = Math.max(peakDayHC, c.dayHC);     // peak across (sequential) phases
      peakNightHC = Math.max(peakNightHC, c.nightHC);
    }
    rows.push({
      craft, cells,
      total: { dayHC: peakDayHC, nightHC: peakNightHC, dayHrs: r1(tDayHrs), nightHrs: r1(tNightHrs), hrs: r1(tDayHrs + tNightHrs) },
    });
  }
  rows.sort((a, b) => b.total.hrs - a.total.hrs || a.craft.localeCompare(b.craft));

  // Grand totals: HC sums across crafts per phase (total bodies on shift);
  // grand peak = max of those per-phase sums (peak combined manpower).
  const grandPhase = {};
  let gDayHrs = 0, gNightHrs = 0, gPeakDay = 0, gPeakNight = 0;
  for (const ph of phases) {
    let dHC = 0, nHC = 0, dHrs = 0, nHrs = 0;
    for (const row of rows) {
      dHC += row.cells[ph].dayHC; nHC += row.cells[ph].nightHC;
      dHrs += row.cells[ph].dayHrs; nHrs += row.cells[ph].nightHrs;
    }
    grandPhase[ph] = { dayHC: dHC, nightHC: nHC, dayHrs: r1(dHrs), nightHrs: r1(nHrs) };
    gDayHrs += dHrs; gNightHrs += nHrs;
    gPeakDay = Math.max(gPeakDay, dHC); gPeakNight = Math.max(gPeakNight, nHC);
  }
  const grand = {
    byPhase: grandPhase,
    total: { dayHC: gPeakDay, nightHC: gPeakNight, dayHrs: r1(gDayHrs), nightHrs: r1(gNightHrs), hrs: r1(gDayHrs + gNightHrs) },
  };

  return { phases, rows, grand };
}
